<script>

    import { toast } from 'bulma-toast'
    import { userStore, usersStore } from '../stores'

    import UsersService from '../$services/users.service'
    import UtilsMethods from '../$methods/utils.methods'

    import Button from '../$components/button.svelte'
    import DetailsCard from '../$components/details.card.svelte'
    import Message from '../$components/message.svelte'
    import Select from '../$components/select.svelte'
    import Spinner from '../$components/spinner.svelte'

    const providersList = UtilsMethods.getProviders()
    const servicesList = UtilsMethods.getServices()

    let loading = false
    let providerSelected = ''
    let serviceDrafts = {}
    let providers = $userStore.providers || []
    let services = $userStore.services || {}

    $: availableProviders = providersList.filter(p => !providers.includes(p))

    async function updateUser() {

        loading = true
        const resp = await UsersService.updateUser($userStore._id, {providers, services})
        loading = false

        if(!resp.success)
            return toast({message: resp.error.message, type: 'is-danger'})

        userStore.set(resp.data)
        usersStore.replace(resp.data)

        providers = resp.data.providers || []
        services = resp.data.services || {}
    }

    function filterProvider(provider) {
        providers = providers.filter(item => item != provider)
        const {[provider]: _, ...rest} = services
        services = rest
        updateUser()
    }

    function addProvider() {

        if(!providerSelected)
            return

        providers = [...new Set([...providers, providerSelected])]
        providerSelected = ''
        updateUser()
    }

    function addService(provider) {

        const selected = serviceDrafts[provider]
        if(!selected)
            return

        if(!services[provider])
            services[provider] = []

        services[provider] = [...new Set([...services[provider], selected])]
        serviceDrafts[provider] = ''
        serviceDrafts = serviceDrafts
        updateUser()
    }

    function removeService(provider, service) {
        services[provider] = services[provider].filter(item => item != service)
        updateUser()
    }

    function serviceOptions(provider) {
        const all = servicesList[provider] || []
        const active = services[provider] || []
        return all
            .filter(s => !active.includes(s))
            .map(s => ({ value: s, text: s }))
    }

    function hasRestrictedServices(provider) {
        return (services[provider]?.length ?? 0) > 0
    }

</script>

<Spinner {loading} />

{#if !loading}
    <DetailsCard
        title="Nuevo proveedor"
        meta="Añade un proveedor activo para este usuario"
    >
        <div class="providers-add">
            <div class="providers-add__field">
                <Select
                    bind:value={providerSelected}
                    label="Proveedor"
                    text="Elegir proveedor…"
                    required={false}
                    options={availableProviders.map(p => ({ value: p, text: p }))}
                />
            </div>
            <div class="providers-add__action">
                <Button
                    on:click={addProvider}
                    text="Agregar proveedor"
                    color="primary"
                    icon="plus"
                    fullwidth={true}
                />
            </div>
        </div>
    </DetailsCard>
{/if}

{#if !loading && !providers.length}
    <Message show={true} compact={true} subtitle="Añade un proveedor para configurar sus servicios" />
{/if}

{#if !loading && providers.length}
    <div class="providers-grid">
        {#each providers as provider}
            <DetailsCard title={provider}>
                <div slot="right">
                    <Button
                        on:click={() => filterProvider(provider)}
                        icon="trash"
                        color="light"
                        tooltip="Eliminar proveedor"
                    />
                </div>

                <section class="providers-services" aria-label="Servicios de {provider}">
                    {#if hasRestrictedServices(provider)}
                        <ul class="providers-services__list">
                            {#each services[provider] as item}
                                <li class="providers-services__item">
                                    <span class="tag is-info providers-services__tag">{ item }</span>
                                    <Button
                                        on:click={() => removeService(provider, item)}
                                        icon="times"
                                        color="light"
                                        size="small"
                                        tooltip="Quitar servicio"
                                    />
                                </li>
                            {/each}
                        </ul>
                    {:else}
                        <p class="providers-services__all">
                            <span class="tag is-light">Todos los servicios</span>
                            <span class="providers-services__hint">Sin restricción por servicio</span>
                        </p>
                    {/if}
                </section>

                {#if serviceOptions(provider).length}
                    <div class="providers-add-service-block">
                        <p class="providers-add-service__label">Añadir servicio</p>
                        <div class="providers-add-service">
                            <div class="providers-add-service__field">
                                <Select
                                    bind:value={serviceDrafts[provider]}
                                    text="Elegir servicio…"
                                    required={false}
                                    options={serviceOptions(provider)}
                                />
                            </div>
                            <div class="providers-add-service__action">
                                <Button
                                    on:click={() => addService(provider)}
                                    text="Añadir"
                                    color="primary"
                                    icon="plus"
                                    fullwidth={true}
                                />
                            </div>
                        </div>
                    </div>
                {:else if hasRestrictedServices(provider)}
                    <p class="providers-add-service__done">Todos los servicios disponibles ya están asignados.</p>
                {/if}
            </DetailsCard>
        {/each}
    </div>
{/if}

<style lang="scss">
    .providers-add {
        display: grid;
        gap: 0.75rem 1rem;
        grid-template-columns: 1fr auto;
        align-items: end;
        padding-top: 0.875rem;

        @media screen and (max-width: 768px) {
            grid-template-columns: 1fr;
        }
    }

    .providers-add__field {
        min-width: 0;
    }

    .providers-add__field :global(.field),
    .providers-add-service__field :global(.field) {
        margin-bottom: 0;
    }

    .providers-add__action {
        flex-shrink: 0;
        min-width: 11rem;

        @media screen and (max-width: 768px) {
            min-width: 0;
        }
    }

    .providers-grid {
        display: grid;
        gap: 1rem;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        margin-top: 1rem;

        @media screen and (max-width: 900px) {
            grid-template-columns: 1fr;
        }
    }

    .providers-services {
        padding-top: 0.875rem;
    }

    .providers-services__list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .providers-services__item {
        align-items: center;
        display: flex;
        gap: 0.5rem;
        justify-content: space-between;
    }

    .providers-services__tag {
        flex: 1;
        justify-content: flex-start;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .providers-services__all {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem 0.75rem;
        margin: 0;
    }

    .providers-services__hint {
        color: var(--til-muted);
        font-size: 0.8125rem;
    }

    .providers-add-service-block {
        border-top: 1px solid var(--til-line);
        margin-top: 1rem;
        padding-top: 1rem;
    }

    .providers-add-service__label {
        color: var(--til-muted);
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.06em;
        margin: 0 0 0.5rem;
        text-transform: uppercase;
    }

    .providers-add-service {
        display: grid;
        gap: 0.5rem 0.75rem;
        grid-template-columns: 1fr auto;
        align-items: end;

        @media screen and (max-width: 480px) {
            grid-template-columns: 1fr;
        }
    }

    .providers-add-service__field {
        min-width: 0;
    }

    .providers-add-service__action {
        min-width: 6.5rem;
    }

    .providers-add-service__done {
        color: var(--til-muted);
        font-size: 0.8125rem;
        margin: 0;
    }
</style>
